/**
 * Who may assign / change Practitioner roles (PRD_ROLE_TRANSITIONS rules 2–3).
 *
 * Pure functions — safe for browser and bots. `edit_users` is a separate gate;
 * this matrix is necessary but not sufficient alone.
 */

import type { UnifyRole } from './resources/practitioner.js';

/** Roles that can appear on PractitionerRole / profile (excludes portal `client`). */
export type AssignableUnifyRole = UnifyRole;

const ALL_ASSIGNABLE: readonly AssignableUnifyRole[] = [
  'unify-admin',
  'unify-assistant',
  'practice-owner',
  'practice-admin',
  'practice-assistant',
  'supervisor',
  'provider',
] as const;

/**
 * Roles an editor may assign to others (and to themselves).
 * Rule 2: only unify-admin may assign practice-owner.
 */
const ASSIGNMENT_TARGETS_BY_EDITOR: Partial<
  Record<AssignableUnifyRole, readonly AssignableUnifyRole[]>
> = {
  'unify-admin': ALL_ASSIGNABLE,
  'unify-assistant': [
    'unify-assistant',
    'practice-admin',
    'practice-assistant',
    'supervisor',
    'provider',
  ],
  'practice-owner': [
    'practice-admin',
    'practice-assistant',
    'supervisor',
    'provider',
  ],
  'practice-admin': ['practice-assistant', 'supervisor', 'provider'],
  'practice-assistant': ['supervisor', 'provider'],
};

function isAssignableRole(role: string): role is AssignableUnifyRole {
  return (ALL_ASSIGNABLE as readonly string[]).includes(role);
}

/** Whether the editor may assign this role slug in isolation. */
export function canEditorAssignRole(
  editorRole: string,
  role: string,
): boolean {
  if (!isAssignableRole(editorRole) || !isAssignableRole(role)) return false;
  const allowed = ASSIGNMENT_TARGETS_BY_EDITOR[editorRole];
  return Boolean(allowed?.includes(role));
}

/**
 * Roles available in a role dropdown for this editor.
 * Always includes the target's current role so the control can show status quo.
 */
export function getAssignableRolesForEditor(
  editorRole: string,
  targetCurrentRole: string,
): AssignableUnifyRole[] {
  if (!isAssignableRole(editorRole)) {
    return isAssignableRole(targetCurrentRole) ? [targetCurrentRole] : [];
  }
  const allowed = ASSIGNMENT_TARGETS_BY_EDITOR[editorRole] ?? [];
  const current = isAssignableRole(targetCurrentRole) ? targetCurrentRole : undefined;
  return [...new Set<AssignableUnifyRole>([...allowed, ...(current ? [current] : [])])];
}

/**
 * Whether the editor may open/edit the role field for this target.
 * Self-assignment is allowed when the editor may assign at least one role
 * (PRD rule 3).
 */
export function isUserRoleFieldEditable(
  editorRole: string,
  targetCurrentRole: string,
  _isSelf: boolean,
): boolean {
  if (!isAssignableRole(editorRole)) return false;
  const allowed = ASSIGNMENT_TARGETS_BY_EDITOR[editorRole];
  if (!allowed?.length) return false;
  // Practice-admin cannot change *other* practice-admins (self OK).
  if (
    !_isSelf
    && editorRole === 'practice-admin'
    && targetCurrentRole === 'practice-admin'
  ) {
    return false;
  }
  return true;
}

export interface RoleChangeAuthorizationInput {
  editorRole: string;
  /** Target's current primary role (omit / empty for invite). */
  targetCurrentRole?: string;
  /** Role being assigned. */
  targetNewRole: string;
  isSelf: boolean;
}

export type RoleChangeDenialCode =
  | 'editor-cannot-assign'
  | 'practice-owner-restricted'
  | 'practice-admin-peer-restricted'
  | 'unknown-role';

export interface RoleChangeAuthorizationResult {
  allowed: boolean;
  code?: RoleChangeDenialCode;
  message?: string;
}

/**
 * Full authorization check for invite or role-change (after edit_users).
 */
export function authorizeRoleChange(
  input: RoleChangeAuthorizationInput,
): RoleChangeAuthorizationResult {
  const { editorRole, targetCurrentRole, targetNewRole, isSelf } = input;

  if (!isAssignableRole(targetNewRole)) {
    return {
      allowed: false,
      code: 'unknown-role',
      message: `Unknown or non-assignable role: ${targetNewRole}`,
    };
  }

  if (!isAssignableRole(editorRole)) {
    return {
      allowed: false,
      code: 'editor-cannot-assign',
      message: `Role ${editorRole} may not assign roles`,
    };
  }

  // Rule 2 — only unify-admin may assign/change practice-owner
  if (targetNewRole === 'practice-owner' && editorRole !== 'unify-admin') {
    return {
      allowed: false,
      code: 'practice-owner-restricted',
      message: 'Only unify-admin may assign or change practice-owner',
    };
  }

  // Practice-admin cannot change other practice-admins
  if (
    !isSelf
    && editorRole === 'practice-admin'
    && targetCurrentRole === 'practice-admin'
  ) {
    return {
      allowed: false,
      code: 'practice-admin-peer-restricted',
      message: 'Practice-admin cannot change other practice-admins',
    };
  }

  if (!canEditorAssignRole(editorRole, targetNewRole)) {
    return {
      allowed: false,
      code: 'editor-cannot-assign',
      message: `${editorRole} may not assign role ${targetNewRole}`,
    };
  }

  return { allowed: true };
}

/** Throws if authorizeRoleChange denies (for bot handlers). */
export function assertCanAssignRole(input: RoleChangeAuthorizationInput): void {
  const result = authorizeRoleChange(input);
  if (!result.allowed) {
    throw new Error(result.message ?? 'Role assignment not permitted');
  }
}
